import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";
import { useBackspaceNavigation } from "../hooks/useBackspaceNavigation";

/** API may return Mongoose Map-like expenses; normalize so ItemName/ItemUnit read reliably */
function mapLikeToPlainObject(val) {
  if (val == null || typeof val !== 'object') return {};
  if (val instanceof Map) return Object.fromEntries(val);
  if (typeof val.forEach === 'function' && !Array.isArray(val)) {
    const plain = {};
    val.forEach((v, k) => {
      plain[k] = v;
    });
    return plain;
  }
  return { ...val };
}

function normalizeBillFromApi(raw) {
  if (!raw) return null;
  return {
    ...raw,
    expenses: mapLikeToPlainObject(raw.expenses),
    sales: mapLikeToPlainObject(raw.sales)
  };
}

// Pakistani Fruits List
const pakistaniFruits = [
  { value: 'aam', label: 'آم', labelEn: 'Mango' },
  { value: 'kela', label: 'کیلا', labelEn: 'Banana' },
  { value: 'santara', label: 'سنتارا', labelEn: 'Orange' },
  { value: 'mosambi', label: 'موسمبی', labelEn: 'Sweet Lime' },
  { value: 'malta', label: 'مالٹا', labelEn: 'Malta' },
  { value: 'chikoo', label: 'چیکو', labelEn: 'Sapodilla' },
  { value: 'amrood', label: 'امرود', labelEn: 'Guava' },
  { value: 'anar', label: 'انار', labelEn: 'Pomegranate' },
  { value: 'angoor', label: 'انگور', labelEn: 'Grapes' },
  { value: 'tarbuz', label: 'تربوز', labelEn: 'Watermelon' },
  { value: 'kharbuza', label: 'خربوزہ', labelEn: 'Muskmelon' },
  { value: 'kharbooza', label: 'خربوزہ', labelEn: 'Cantaloupe' },
  { value: 'seb', label: 'سیب', labelEn: 'Apple' },
  { value: 'nashpati', label: 'ناشپاتی', labelEn: 'Pear' },
  { value: 'alubukhara', label: 'آلو بخارا', labelEn: 'Plum' },
  { value: 'khubani', label: 'خوبانی', labelEn: 'Apricot' },
  { value: 'aalu', label: 'آلو', labelEn: 'Potato' },
  { value: 'tamatar', label: 'ٹماٹر', labelEn: 'Tomato' },
  { value: 'kakri', label: 'ککڑی', labelEn: 'Cucumber' },
  { value: 'tori', label: 'ٹوری', labelEn: 'Ridge Gourd' },
  { value: 'lauki', label: 'لوکی', labelEn: 'Bottle Gourd' },
  { value: 'kaddu', label: 'کدو', labelEn: 'Pumpkin' },
  { value: 'bhindi', label: 'بھنڈی', labelEn: 'Okra' },
  { value: 'baingan', label: 'بینگن', labelEn: 'Brinjal' },
  { value: 'shimla_mirch', label: 'شملہ مرچ', labelEn: 'Capsicum' },
  { value: 'gajar', label: 'گاجر', labelEn: 'Carrot' },
  { value: 'mooli', label: 'مولی', labelEn: 'Radish' },
  { value: 'pyaaz', label: 'پیاز', labelEn: 'Onion' },
  { value: 'adrak', label: 'ادرک', labelEn: 'Ginger' },
  { value: 'lehsan', label: 'لہسن', labelEn: 'Garlic' },
  { value: 'palak', label: 'پالک', labelEn: 'Spinach' },
  { value: 'methi', label: 'میتھی', labelEn: 'Fenugreek' },
  { value: 'dhaniya', label: 'دھنیا', labelEn: 'Coriander' },
  { value: 'podina', label: 'پودینہ', labelEn: 'Mint' },
  { value: 'hari_mirch', label: 'ہری مرچ', labelEn: 'Green Chili' },
  { value: 'limoo', label: 'لیمو', labelEn: 'Lemon' },
  { value: 'nimbu', label: 'نیمبو', labelEn: 'Lime' },
  { value: 'kino', label: 'کینو', labelEn: 'Kinnow' },
  { value: 'falsa', label: 'فالسہ', labelEn: 'Phalsa' },
  { value: 'jamun', label: 'جامن', labelEn: 'Java Plum' },
  { value: 'ber', label: 'بیر', labelEn: 'Jujube' },
  { value: 'amla', label: 'آملہ', labelEn: 'Indian Gooseberry' },
  { value: 'sharifa', label: 'شریفہ', labelEn: 'Custard Apple' },
  { value: 'papita', label: 'پپیتا', labelEn: 'Papaya' },
  { value: 'ananas', label: 'انناس', labelEn: 'Pineapple' },
  { value: 'zaitoon', label: 'زیتون', labelEn: 'Olive' },
];

// Mapping for delivery method codes to Urdu labels (for Bill view)
const deliveryMethodLabelsUrdu = {
  crates: 'کریٹس',
  bags: 'بوریاں / بیگز',
  boxes: 'بکس / کارٹن',
  pallets: 'پیلیٹس',
  loose: 'کھلے ٹرک',
  small_van: 'ٹرالی / چھوٹی گاڑیاں',
  tractor: 'ٹرک/ٹرکٹر ٹرالی',
  container: 'کنٹینر / بڑے ٹرک',
  bulk_bags: 'بیگڈ پیکنگ',
  cold_truck: 'فریزر / کولر وین',
  logistics: 'ٹرانسپورٹ کمپنی',
  self: 'خود لایا گیا',
  agent: 'کمیشن ایجنٹ کے ذریعے',
};

// Mapping for item unit codes to labels
const itemUnitLabels = {
  units: { en: 'Units', ur: 'یونٹس' },
  crates: { en: 'Crates', ur: 'کریٹس' },
  bags: { en: 'Bags', ur: 'بوریاں' },
  pali: { en: 'Pali', ur: 'پلی' },
  paiti: { en: 'Paiti', ur: 'پیتی' },
  // Case variations
  'Pali': { en: 'Pali', ur: 'پلی' },
  'PALI': { en: 'Pali', ur: 'پلی' },
  'Paiti': { en: 'Paiti', ur: 'پیتی' },
  'PAITI': { en: 'Paiti', ur: 'پیتی' },
};

function BillView() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Backspace navigation to dashboard
  useBackspaceNavigation();
  
  const [bill, setBill] = useState(null);
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [language, setLanguage] = useState('en'); // 'en', 'ur', or 'both'
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isCompanyUser, setIsCompanyUser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expenseFields, setExpenseFields] = useState([]);
  const [salesFields, setSalesFields] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [paidDate, setPaidDate] = useState('');
  const billContentRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  // Debug: Log when component mounts (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    console.log('🚀 BillView component mounted/updated');
    console.log('Bill ID:', billId);
    console.log('Current language:', language);
    }
  }, [billId, bill, expenseFields, language]);

  // If ?lang=en or ?lang=ur is present in URL, force that language
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const forcedLang = params.get('lang');
    if (forcedLang === 'en' || forcedLang === 'ur') {
      setLanguage(forcedLang);
      setCompanyLanguage(forcedLang);
    }
  }, [location.search]);

  useEffect(() => {
    fetchBill();
  }, [billId]);

  // Handle Enter key to navigate to create bill form
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if Enter is pressed and not in an input/textarea/button
      if (e.key === 'Enter' && !e.target.matches('input, textarea, button, select')) {
        e.preventDefault();
        // Check if company uses part2 dashboard, otherwise use regular create bill
        const dashboardType = company?.dashboardType || company?.dashboard_type || company?.dashboard || company?.isPart2Dashboard || company?.isDashboardPart2;
        if (dashboardType === 'part2') {
          navigate('/bill/create-part2');
        } else {
        navigate('/bill/create');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, company]);

  // Sync expenseFields when bill changes
  useEffect(() => {
    if (bill && bill.expenseFields && bill.expenseFields.length > 0) {
      console.log('=== SYNCING EXPENSE FIELDS ===');
      console.log('Original bill.expenseFields:', bill.expenseFields);

      // Ensure Labor and Miscellaneous always have Urdu names - FORCE it regardless of existing fieldNameUrdu
      const normalizedFields = bill.expenseFields.map(field => {
        console.log('Processing field:', {
          fieldName: field.fieldName,
          fieldNameUrdu: field.fieldNameUrdu,
          fullField: field
        });

        // Always force Urdu for Labor/Wages
        if (field.fieldName === 'Labor' || field.fieldName === 'Wages') {
          const normalized = { ...field, fieldName: 'Labor', fieldNameUrdu: 'مزدوری' };
          console.log('✓ Normalized Labor/Wages to:', normalized);
          return normalized;
        }
        // Always force Urdu for Miscellaneous/Mashiyana
        if (field.fieldName === 'Miscellaneous' || field.fieldName === 'Mashiyana') {
          const normalized = { ...field, fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ' };
          console.log('✓ Normalized Miscellaneous/Mashiyana to:', normalized);
          return normalized;
        }
        return field;
      });

      console.log('Final normalized expenseFields:', normalizedFields);
      console.log('Fields with Labor/Miscellaneous:', normalizedFields.filter(f =>
        f.fieldName === 'Labor' || f.fieldName === 'Wages' ||
        f.fieldName === 'Miscellaneous' || f.fieldName === 'Mashiyana'
      ));

      setExpenseFields(normalizedFields);
    }
    if (bill && bill.salesFields && bill.salesFields.length > 0) {
      setSalesFields(bill.salesFields);
    }
  }, [bill]);

  const fetchBill = async () => {
    try {
      setIsLoading(true);
      // Try company token first, then admin token
      const companyToken = localStorage.getItem("companyToken");
      const adminToken = localStorage.getItem("adminToken");
      const token = companyToken || adminToken;

      if (!token) {
        toast.error("Please login to view bill");
        return;
      }

      const response = await api.get(`/bill/company/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const raw = response.data.bill;
      const billNormalized = normalizeBillFromApi(raw);

      if (!billNormalized) {
        setBill(null);
        setExpenseFields([]);
        setSalesFields([]);
        return;
      }

      setBill(billNormalized);

      // Set expense and sales fields for drag and drop
      if (billNormalized.expenseFields && billNormalized.expenseFields.length > 0) {
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
        console.log('=== FETCHING BILL - EXPENSE FIELDS ===');
        }

        // Ensure Labor and Miscellaneous always have Urdu names - FORCE it regardless of existing fieldNameUrdu
        const normalizedFields = billNormalized.expenseFields.map(field => {
          // Only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('Processing field:', {
            fieldName: field.fieldName,
              fieldNameUrdu: field.fieldNameUrdu
          });
          }

          // Always force Urdu for Labor/Wages
          if (field.fieldName === 'Labor' || field.fieldName === 'Wages') {
            const normalized = { ...field, fieldName: 'Labor', fieldNameUrdu: 'مزدوری' };
            if (process.env.NODE_ENV === 'development') {
              console.log('✓ Normalized Labor/Wages');
            }
            return normalized;
          }
          // Always force Urdu for Miscellaneous/Mashiyana
          if (field.fieldName === 'Miscellaneous' || field.fieldName === 'Mashiyana') {
            const normalized = { ...field, fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ' };
            if (process.env.NODE_ENV === 'development') {
              console.log('✓ Normalized Miscellaneous/Mashiyana');
            }
            return normalized;
          }
          return field;
        });

        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Final normalized expenseFields');
        }

        setExpenseFields(normalizedFields);
      } else {
        // Initialize with empty array if no fields
        setExpenseFields([]);
      }
      if (billNormalized.salesFields && billNormalized.salesFields.length > 0) {
        setSalesFields(billNormalized.salesFields);
      } else {
        setSalesFields([]);
      }

      // Check if user is company (not admin)
      setIsCompanyUser(!!companyToken);
      setIsAdmin(!!adminToken);

      // Fetch company info
      if (billNormalized.companyId) {
        try {
          let companyResponse;
          // If user is company, use company profile endpoint
          if (companyToken) {
            companyResponse = await api.get(`/company/profile`, {
              headers: { Authorization: `Bearer ${companyToken}` }
            });
          }
          // If user is admin, use admin endpoint
          else if (adminToken) {
            companyResponse = await api.get(`/admin/companies/${billNormalized.companyId}`, {
              headers: { Authorization: `Bearer ${adminToken}` }
            });
          }

          if (companyResponse && companyResponse.data.company) {
            setCompany(companyResponse.data.company);
            const companyLang = companyResponse.data.company?.language || 'en';
            setLanguage(companyLang);
            setCompanyLanguage(companyLang);
          } else {
            // Fallback: use language from bill's templateSettings if available
            const billLanguage = billNormalized.templateSettings?.language || 'en';
            setLanguage(billLanguage);
          }
        } catch (err) {
          console.log("Could not fetch company details, using bill data");
          // Fallback: use language from bill's templateSettings if available
          const billLanguage = billNormalized.templateSettings?.language || 'en';
          setLanguage(billLanguage);
        }
      } else {
        // If no companyId, try to get language from templateSettings
        const billLanguage = billNormalized.templateSettings?.language || 'en';
        setLanguage(billLanguage);
      }
    } catch (error) {
      console.error("Fetch bill error:", error);
      toast.error("Failed to load bill");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading bill...</p>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Bill not found</p>
        </div>
      </div>
    );
  }

  const template = bill.templateSettings || {};
  const showBoth = language === 'both';

  // Calculate totals (exclude Vehicle, ItemName, ItemUnit, ItemQuantity, خام بکری, اخراجات, پختہ بکری, and laga as they're not actual expenses)
  let totalExpenses = 0;
  if (bill.expenses) {
    Object.keys(bill.expenses).forEach(key => {
      const value = bill.expenses[key];
      // Skip non-expense fields: Vehicle, ItemName, ItemUnit, ItemQuantity, ActualVehicle, calculated fields, and laga
      const isNonExpenseField = 
        key === 'Vehicle' || 
        key === 'ActualVehicle' ||
        key === 'ItemName' || 
        key === 'ItemUnit' || 
        key === 'ItemQuantity' ||
        key === 'خام بکری' ||
        key === 'اخراجات' ||
        key === 'پختہ بکری' ||
        key === 'لاگا' || // Exclude laga from expenses (it goes to amdani only)
        key === 'Laga' ||
        key === 'DeliveryMethod' ||
        key === 'GoodsMessageUrdu';
      
      if (!isNonExpenseField && typeof value === 'number' && !isNaN(value)) {
        totalExpenses += value;
      }
    });
  }

  const rawSale = bill.sales?.rawSale || bill.sales?.['Raw Sale'] || bill.rawSale || 0;
  // Recalculate netSale using correct totalExpenses (excluding خام بکری, اخراجات, پختہ بکری)
  // Don't use bill.netSale as it might have been calculated with wrong totalExpenses
  const netSale = rawSale - totalExpenses;


  // Handle drag end for expense fields (Admin only)
  const onExpenseFieldsDragEnd = async (result) => {
    console.log("Drag end triggered:", result);
    console.log("isAdmin:", isAdmin, "bill:", !!bill);

    if (!result.destination) {
      console.log("No destination, cancelling");
      return;
    }

    if (!isAdmin || !bill) {
      console.log("Not admin or no bill, cancelling");
      return;
    }

    // Use current expenseFields or fallback to bill.expenseFields
    const currentFields = expenseFields.length > 0 ? expenseFields : (bill.expenseFields || []);
    console.log("Current fields:", currentFields);
    const items = Array.from(currentFields);

    // Don't do anything if dropped in the same position
    if (result.destination.index === result.source.index) {
      console.log("Same position, no change needed");
      return;
    }

    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    // Optimistically update UI
    setExpenseFields(updatedItems);

    // Update bill in backend
    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.put(`/bill/admin/${billId}`, {
        expenseFields: updatedItems
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBill(normalizeBillFromApi(response.data.bill));
      // Update state with response data
      if (response.data.bill.expenseFields) {
        setExpenseFields(response.data.bill.expenseFields);
      }
      toast.success("Fields reordered successfully!");
    } catch (error) {
      console.error("Update fields error:", error);
      toast.error("Failed to update field order");
      // Revert on error
      setExpenseFields(bill.expenseFields || []);
    }
  };

  // Handle drag end for sales fields (Admin only)
  const onSalesFieldsDragEnd = async (result) => {
    if (!result.destination || !isAdmin || !bill) return;

    const items = Array.from(salesFields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    setSalesFields(updatedItems);

    // Update bill in backend
    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.put(`/bill/admin/${billId}`, {
        salesFields: updatedItems
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBill(normalizeBillFromApi(response.data.bill));
      toast.success("Fields reordered successfully!");
    } catch (error) {
      console.error("Update fields error:", error);
      toast.error("Failed to update field order");
      // Revert on error
      setSalesFields(bill.salesFields || []);
    }
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!billContentRef.current) {
      toast.error("Bill content not found");
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info("Generating PDF...");

      const element = billContentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Bill_${bill.serialNumber || bill._id.slice(-6)}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <>
      <Helmet><title>Bill #{bill.serialNumber || bill._id.slice(-6)}</title></Helmet>

      {/* Company Header - Fixed at Top */}
      {/* {company && isCompanyUser && (
        <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage} 
          onLogout={handleLogout} 
        />
      )} */}

      <div className="min-h-screen bg-gray-50 px-2 sm:px-4 pt-[6px] pb-2 sm:pb-4">
        <div className="max-w-4xl mx-auto bg-white shadow-lg border-l-2 sm:border-l-4 border-r-2 sm:border-r-4 border-black print:border-0">
          {/* Print Controls */}
          <div className="p-2 sm:p-4 bg-gray-100 border-b print:hidden">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base ${language === 'en' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('ur')}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base ${language === 'ur' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
                >
                  Urdu
                </button>
                {showBoth && (
                  <button
                    onClick={() => setLanguage('both')}
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base ${language === 'both' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    Both
                  </button>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg disabled:opacity-50 flex items-center gap-2 text-sm sm:text-base flex-1 sm:flex-initial justify-center"
                >
                  {isGeneratingPDF ? (
                    <>
                      <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Generating...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="hidden sm:inline">Download PDF</span>
                      <span className="sm:hidden">PDF</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => window.print()}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base flex-1 sm:flex-initial"
                >
                  <span className="hidden sm:inline">Print Bill</span>
                  <span className="sm:hidden">Print</span>
                </button>
                <button
                  onClick={() => {
                    setIsPaid(!isPaid);
                    if (!isPaid) {
                      setPaidDate(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }));
                    }
                  }}
                  className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg font-semibold text-sm sm:text-base flex items-center gap-2 flex-1 sm:flex-initial justify-center ${
                    isPaid 
                      ? 'bg-green-600 hover:bg-green-700 text-white' 
                      : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden sm:inline">{isPaid ? 'Remove Paid Stamp' : 'Add Paid Stamp'}</span>
                  <span className="sm:hidden">{isPaid ? 'Remove' : 'Paid'}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Bill Content - A4 Size Container */}
          <div className="flex justify-center items-start bg-gray-100 min-h-screen py-4 sm:py-6 bill-a4-wrapper">
            <div
              ref={billContentRef}
              className="bg-white shadow-2xl bill-content-print bill-a4-container relative"
              style={{
                fontFamily: language === 'ur' || showBoth ? "'Noto Nastaliq Urdu', 'Al Qalam Taj Nastaleeq', serif" : 'inherit',
                width: '210mm',
                minHeight: '297mm',
                maxWidth: '100%',
                padding: '1rem'
              }}
            >
              {/* Header - Image Box */}
              <div className="mb-4 sm:mb-6 relative overflow-hidden mx-auto border-2 border-gray-800" style={{
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                minHeight: '250px',
                aspectRatio: '1200 / 196',
                marginTop: '-0.5rem'
              }}>
                {/* Show image - Priority: company.billHeaderImage > headerBackgroundImage > default bg1 > logo > gradient */}
                {(company?.billHeaderImage || (template.headerBackgroundImage && template.headerBackground === 'image') || template.logo) ? (
                  <>
                    <img
                      src={company?.billHeaderImage || template.headerBackgroundImage || template.logo || '/bg1.png'}
                      alt="Header Image"
                      className="absolute inset-0 w-full h-full"
                      style={{ 
                        zIndex: 1, 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                      onError={(e) => {
                        // Fallback to default bg1 if image fails to load
                        if (e.target.src !== '/bg1.png' && e.target.src !== '/bg1.jpg' && e.target.src !== '/bg1.jpeg') {
                          e.target.src = '/bg1.png';
                        }
                      }}
                    />
                    {/* Overlay for better text readability - Hidden since no text */}
                    {/* <div className="absolute inset-0 bg-black bg-opacity-30 rounded-lg" style={{ zIndex: 2 }}></div> */}
                  </>
                ) : (
                  <>
                    {/* Default bg1 image from public folder */}
                    <img
                      src="/bg1.png"
                      alt="Default Background"
                      className="absolute inset-0 w-full h-full"
                      style={{ 
                        zIndex: 1, 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                      onError={(e) => {
                        // If bg1.png doesn't exist, try other formats or fallback to gradient
                        if (e.target.src.endsWith('.png')) {
                          e.target.src = '/bg1.jpg';
                        } else if (e.target.src.endsWith('.jpg')) {
                          e.target.src = '/bg1.jpeg';
                        } else {
                          // Fallback to gradient
                          e.target.style.display = 'none';
                          const gradientDiv = e.target.nextElementSibling;
                          if (gradientDiv) gradientDiv.style.display = 'block';
                        }
                      }}
                    />
                    {/* Fallback gradient if bg1 image not found */}
                    <div
                      className="absolute inset-0"
                      style={{
                        display: 'none',
                        background: template.headerBackground === 'solid'
                          ? template.headerBackgroundColor
                          : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)',
                        zIndex: 1,
                        width: '100%',
                        height: '100%'
                      }}
                    ></div>
                    {/* Background Fruit Images Effect - Hidden */}
                    {/* <div className="absolute inset-0 opacity-10 rounded-lg" style={{ zIndex: 2 }}>
                      <div className="absolute top-0 left-0 w-32 h-32">🍎🍊🍌</div>
                      <div className="absolute bottom-0 right-0 w-32 h-32">🍇🍓🥭</div>
                    </div> */}
                  </>
                )}

                {/* Original Header Structure: Logo left, Company info right - ALL HIDDEN */}
                {/* Only image shows, no text or logo */}
                {/* <div className="relative flex flex-col sm:flex-row items-start gap-3 sm:gap-4 md:gap-6" style={{ zIndex: 10 }}>
                  {/* Left Side - Logo (Always show if logo exists) */}
                  {/* {template.logo && (
                    <div className="flex-shrink-0 mx-auto sm:mx-0">
                      <img
                        src={template.logo}
                        alt="Logo"
                        className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain rounded-lg bg-white p-1 sm:p-2 shadow-lg"
                      />
                    </div>
                  )}

                  {/* Right Side - Company Info (Original) */}
                  {/* <div className="flex-1 text-white text-center sm:text-left">
                    <div className="mb-2 sm:mb-4">
                      {/* Company Name Hidden */}
                      {/* {(language === 'en' || showBoth) && (
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 drop-shadow-lg">{template.companyName || 'Company Name'}</h1>
                      )}
                      {(language === 'ur' || showBoth) && (
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2 drop-shadow-lg" dir="rtl">{template.companyNameUrdu || 'کمپنی کا نام'}</h1>
                      )} */}
                      {/* {(language === 'en' || showBoth) && (
                        <p className="text-sm sm:text-base md:text-lg font-semibold drop-shadow-md">{template.businessType || 'Fruit Commission Agent'}</p>
                      )}
                      {(language === 'ur' || showBoth) && (
                        <p className="text-sm sm:text-base md:text-lg font-semibold drop-shadow-md" dir="rtl">{template.businessTypeUrdu || 'فروٹ کمیشن ایجنٹ'}</p>
                      )}
                    </div>

                    {/* Contact Info Bar - Green Bar */}
                    {/* {template.contactInfo && template.contactInfo.length > 0 && (
                      <div className="bg-green-600 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
                        <div className="flex flex-wrap gap-2 sm:gap-4 text-white text-xs sm:text-sm font-medium justify-center sm:justify-start">
                          {template.contactInfo.map((contact, idx) => (
                            <div key={idx} className="flex items-center gap-1 sm:gap-2">
                              {(language === 'en' || showBoth) && (
                                <span>{contact.name}: {contact.phone}</span>
                              )}
                              {(language === 'ur' || showBoth) && (
                                <span dir="rtl">{contact.nameUrdu || contact.name}: {contact.phone}</span>
                              )}
                              {idx < template.contactInfo.length - 1 && <span className="hidden sm:inline">|</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Address */}
                    {/* {(template.address || template.addressUrdu) && (
                      <div className="text-xs sm:text-sm">
                        {(language === 'en' || showBoth) && template.address && (
                          <p className="drop-shadow-md">{template.address}</p>
                        )}
                        {(language === 'ur' || showBoth) && template.addressUrdu && (
                          <p className="drop-shadow-md" dir="rtl">{template.addressUrdu}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div> */}
              </div>

              {/* Bill Details - Matching Image Layout (RTL for Urdu) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6 bill-fields-container" style={{ marginBottom: '2rem' }}>
                {/* Left Side - Professional Column Layout */}
                <div className="space-y-2 sm:space-y-3">
                  {/* Date Field */}
                  <div>
                    {(language === 'en' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="ltr">
                        <strong className="text-sm sm:text-base">Date -</strong>
                        <span className="inline-block min-w-[100px] sm:min-w-[120px] text-left text-sm sm:text-base">
                          {new Date(bill.date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {(language === 'ur' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="rtl">
                        <strong className="text-sm sm:text-base">تاریخ -</strong>
                        <span className="border-b-2 border-dotted border-gray-400 inline-block min-w-[100px] sm:min-w-[120px] text-right text-sm sm:text-base">
                          {new Date(bill.date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Gardi Number Field */}
                  <div>
                    {(language === 'en' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="ltr">
                        <strong className="text-sm sm:text-base">Gardi Number -</strong>
                        <span className="inline-block min-w-[100px] sm:min-w-[120px] text-left text-sm sm:text-base">
                          {bill.expenses?.Vehicle || bill.expenses?.ActualVehicle || ''}
                        </span>
                      </div>
                    )}
                    {(language === 'ur' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="rtl">
                        <strong className="text-sm sm:text-base">گاڑی نمبر -</strong>
                        <span className="inline-block min-w-[100px] sm:min-w-[120px] text-right text-sm sm:text-base">
                          {bill.expenses?.Vehicle || bill.expenses?.ActualVehicle || ''}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Item Name Field */}
                  <div>
                    {(language === 'en' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="ltr">
                        <strong className="text-sm sm:text-base">Item Name -</strong>
                        <span className="inline-block min-w-[150px] sm:min-w-[200px] text-left text-sm sm:text-base">
                          {bill.expenses?.ItemName || ''}
                          {(bill.expenses?.ItemUnit || bill.expenses?.ItemQuantity) && (
                            <span className="text-gray-500">
                              {' ('}
                              {bill.expenses?.ItemUnit && (itemUnitLabels[bill.expenses.ItemUnit]?.en || bill.expenses.ItemUnit)}
                              {bill.expenses?.ItemUnit && bill.expenses?.ItemQuantity && ', '}
                              {bill.expenses?.ItemQuantity}
                              {')'}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {(language === 'ur' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="rtl">
                        <strong className="text-sm sm:text-base">چیز کا نام -</strong>
                        <span className="inline-block min-w-[150px] sm:min-w-[200px] text-right text-sm sm:text-base">
                          {bill.expenses?.ItemName || ''}
                          {(bill.expenses?.ItemUnit || bill.expenses?.ItemQuantity) && (
                            <span className="text-gray-500">
                              {' ('}
                              {bill.expenses?.ItemUnit && (() => {
                                const unitKey = (bill.expenses.ItemUnit || '').toLowerCase();
                                return itemUnitLabels[unitKey]?.ur || itemUnitLabels[bill.expenses.ItemUnit]?.ur || bill.expenses.ItemUnit;
                              })()}
                              {bill.expenses?.ItemUnit && bill.expenses?.ItemQuantity && '، '}
                              {bill.expenses?.ItemQuantity}
                              {')'}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Received Message */}
                  <div>
                    {(language === 'en' || showBoth) && (
                      <div
                        className="text-red-600 font-semibold"
                        dangerouslySetInnerHTML={{ __html: bill.receivedMessage || '<strong>Received, thanks for selling</strong>' }}
                      />
                    )}
                  </div>
                </div>

                {/* Right Side - Professional Column Layout */}
                <div className="space-y-2 sm:space-y-3" dir="rtl">
                  {/* Serial Number Field */}
                  <div>
                    {(language === 'en' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="ltr">
                        <strong className="text-sm sm:text-base">Serial No -</strong>
                        <span className="inline-block min-w-[80px] sm:min-w-[100px] text-left text-sm sm:text-base">{bill.serialNumber || ''}</span>
                      </div>
                    )}
                    {(language === 'ur' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="rtl">
                        <strong className="text-sm sm:text-base">سیریل نمبر -</strong>
                        <span className="inline-block min-w-[80px] sm:min-w-[100px] text-right text-sm sm:text-base">{bill.serialNumber || ''}</span>
                      </div>
                    )}
                  </div>

                  {/* Customer Name Field */}
                  <div>
                    {(language === 'en' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="ltr">
                        <strong className="text-sm sm:text-base">Customer Name -</strong>
                        <span className="inline-block min-w-[150px] sm:min-w-[200px] text-left text-sm sm:text-base">
                          {bill.customerName || ''}
                        </span>
                      </div>
                    )}
                    {(language === 'ur' || showBoth) && (
                      <div className="text-gray-700 flex items-center gap-12" dir="rtl">
                        <strong className="text-sm sm:text-base">کسٹمر کا نام -</strong>
                        <span className="inline-block min-w-[150px] sm:min-w-[200px] text-right text-sm sm:text-base">
                          {bill.customerNameUrdu || bill.customerName || ''}
                        </span>
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* Expenses Section - Right Side as per image (RTL for Urdu) */}
              {/* md:-mr-8 se poora block thora sa aur right border ki taraf aa jayega */}
              <div className="mb-4 sm:mb-6 md:-mr-8 border-t-2 border-gray-400 pt-3">
                {/* 2-column grid: equal space for both columns */}
                {/* Expense details on top, Sales summary at bottom */}
                <div className="flex flex-col md:flex-row gap-4 sm:gap-6 print-desktop-layout">
                  {/* Sales Fields Column - Left side (Desktop), Bottom (Mobile) */}
                  <div className="flex-1 order-2 md:order-1 print-sales-left pr-4 sm:pr-6 relative">
                    {/* Paid Stamp - Top of Raw Sale Column */}
                    {isPaid && (
                      <div
                        className="absolute"
                        style={{
                          top: '40px',
                          left: '50%',
                          transform: 'translateX(-50%) rotate(-15deg)',
                          zIndex: 1000,
                          opacity: 0.9
                        }}
                      >
                        <div
                          style={{
                            border: '6px solid red',
                            borderRadius: '6px',
                            padding: '10px 18px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                            textAlign: 'center',
                            minWidth: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                          }}
                        >
                          {/* Top label: AS */}
                          <div
                            style={{
                              fontSize: '18px',
                              fontWeight: '800',
                              color: 'red',
                              letterSpacing: '4px',
                              marginBottom: '2px',
                              textTransform: 'uppercase',
                              lineHeight: 1
                            }}
                          >
                            AS
                          </div>
                          {/* Main PAID text */}
                          <div
                            style={{
                              fontSize: '40px',
                              fontWeight: 'bold',
                              color: 'red',
                              letterSpacing: '4px',
                              lineHeight: '1.1',
                              marginBottom: '6px',
                              textDecoration: 'underline'
                            }}
                          >
                            PAID
                          </div>
                          {paidDate && (
                            <div
                              style={{
                                fontSize: '16px',
                                color: '#333',
                                fontWeight: '600',
                                borderTop: '2px solid red',
                                paddingTop: '8px',
                                marginTop: '4px'
                              }}
                            >
                              {paidDate}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Vertical line - limited height from top */}
                    <div className="absolute top-0 right-0 w-0.5 bg-gray-300 vertical-line-column" style={{ height: 'calc(100% - 4rem)' }}></div>
                    {/* Sales summary: give padding on both sides so it's not touching center/border */}
                    <div className="space-y-3 sm:space-y-4 pl-3 sm:pl-6 pr-0 md:mt-[14rem] print-sales-margin">
                      {/* Raw Sale row */}
                      {language === 'en' && !showBoth ? (
                        <div className="flex items-end justify-between gap-6 mt-64">
                          {/* Label left */}
                          <div className="flex-1 text-left">
                            <span className="font-bold text-red-600">Raw Sale</span>
                          </div>
                          {/* Value right */}
                          <div className="min-w-[60px] text-right">
                            <span className="font-bold text-gray-900">
                              {typeof rawSale === 'number' ? rawSale.toLocaleString() : rawSale}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-end justify-between gap-12 mt-64">
                          {/* Value left side (Urdu layout) */}
                          <div className="min-w-[60px] text-right">
                            <span className="font-bold text-gray-900">
                              {typeof rawSale === 'number' ? rawSale.toLocaleString() : rawSale}
                            </span>
                          </div>
                          {/* Text right side aligned with border */}
                          <div className="flex-1 text-right pr-0">
                            {(language === 'en' || showBoth) && (
                              <span className="font-bold text-red-600">Raw Sale</span>
                            )}
                            {(language === 'ur' || showBoth) && (
                              <span className="font-bold text-red-600" dir="rtl">خام بِکری</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Expenses total row */}
                      {language === 'en' && !showBoth ? (
                        <div className="flex items-end justify-between gap-6">
                          {/* Label left */}
                          <div className="flex-1 text-left">
                            <span className="font-bold text-red-600">Expenses</span>
                          </div>
                          {/* Value right */}
                          <div className="min-w-[60px] text-right">
                            <span className="font-bold text-gray-900">
                              {typeof totalExpenses === 'number' ? totalExpenses.toLocaleString() : totalExpenses}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-end justify-between gap-6">
                          {/* Value left side (Urdu layout) */}
                          <div className="min-w-[60px] text-right">
                            <span className="font-bold text-gray-900">
                              {typeof totalExpenses === 'number' ? totalExpenses.toLocaleString() : totalExpenses}
                            </span>
                          </div>
                          {/* Text right side aligned with border */}
                          <div className="flex-1 text-right pr-0">
                            {(language === 'en' || showBoth) && (
                              <span className="font-bold text-red-600">Expenses</span>
                            )}
                            {(language === 'ur' || showBoth) && (
                              <span className="text-lg sm:text-xl font-bold text-red-600" dir="rtl">اخراجات</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Net Sale row */}
                      {language === 'en' && !showBoth ? (
                        <div className="flex items-end justify-between gap-6 border-t-2 border-gray-400 pt-3">
                          {/* Label left */}
                          <div className="flex-1 text-left">
                            <span className="font-bold text-red-600 text-lg">Net Sale</span>
                          </div>
                          {/* Value right */}
                          <div className="min-w-[60px] text-right">
                            <span className="font-bold text-gray-900 text-lg">
                              {typeof netSale === 'number' ? netSale.toLocaleString() : netSale}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-end justify-between gap-6 border-t-2 border-gray-400 pt-3">
                          {/* Value left side (Urdu layout) */}
                          <div className="min-w-[60px] text-right">
                            <span className="font-bold text-gray-900 text-lg">
                              {typeof netSale === 'number' ? netSale.toLocaleString() : netSale}
                            </span>
                          </div>
                          {/* Text right side aligned with border */}
                          <div className="flex-1 text-right pr-0">
                            {(language === 'en' || showBoth) && (
                              <span className="font-bold text-red-600 text-lg">Net Sale</span>
                            )}
                            {(language === 'ur' || showBoth) && (
                              <span className="font-bold text-red-600 text-base sm:text-lg" dir="rtl">پختہ بِکری</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Signature - Below text in left column */}
                      <div className="mt-24 pt-32 text-right">
                        {(language === 'en' || showBoth) && (
                          <p className="text-red-600 font-semibold inline-block text-sm sm:text-base">
                            Signature: <span className="border-b-2 border-red-600 inline-block" style={{ width: '200px', minWidth: '200px' }}></span>
                          </p>
                        )}
                        {(language === 'ur' || showBoth) && (
                          <p className="text-red-600 font-semibold inline-block text-sm sm:text-base" dir="rtl">
                            دستخط: <span className="border-b-2 border-red-600 inline-block" style={{ width: '200px', minWidth: '200px' }}></span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expense Fields Column - Right side (Desktop), Top (Mobile) */}
                  <div className="flex-1 order-1 md:order-2 print-expenses-right pl-4 sm:pl-6">
                    {/* Urdu ke liye RTL, English ke liye LTR + left labels */}
                    <div
                      className={`space-y-3 sm:space-y-4 pr-3 sm:pr-6 md:pr-12 ${language === 'en' && !showBoth ? 'text-left' : 'text-right'
                        }`}
                      dir={language === 'en' && !showBoth ? 'ltr' : 'rtl'}
                    >
                      {/* Heading inside same column so list starts exactly under it */}
                      <div className="mb-2 mt-6 sm:mt-8 text-center">
                        {(language === 'en' || showBoth) && (
                          <h2 className="text-lg sm:text-xl font-bold text-red-600" dir="ltr">Details of Expenses -</h2>
                        )}
                        {(language === 'ur' || showBoth) && (
                          <h2 className="text-lg sm:text-xl font-bold text-red-600" dir="rtl">تفصیل اخراجات -</h2>
                        )}
                      </div>

                      {(() => {
                        // If expenseFields is empty, create fields from bill.expenses
                        if ((!expenseFields || expenseFields.length === 0) && (!bill.expenseFields || bill.expenseFields.length === 0) && bill.expenses) {
                          console.log('⚠️ expenseFields is empty, creating from bill.expenses');
                          const fieldsFromExpenses = Object.keys(bill.expenses)
                            .filter(key => {
                              const keyLower = key.toLowerCase();
                              const keyUrdu = key.replace(/\s/g, '');
                              // Exclude non-expense fields
                              return key !== 'Vehicle' &&
                                key !== 'ActualVehicle' &&
                                key !== 'ItemName' &&
                                key !== 'ItemUnit' &&
                                key !== 'ItemQuantity' &&
                                key !== 'DeliveryMethod' &&
                                key !== 'GoodsMessageUrdu' &&
                                // Exclude Raw Sale, Expenses, Net Sale fields
                                keyLower !== 'raw sale' &&
                                keyLower !== 'rawsale' &&
                                keyLower !== 'expenses' &&
                                keyLower !== 'net sale' &&
                                keyLower !== 'netsale' &&
                                key !== 'Raw Sale' &&
                                key !== 'Expenses' &&
                                key !== 'Net Sale' &&
                                !keyUrdu.includes('خامبکری') &&
                                !keyUrdu.includes('اخراجات') &&
                                !keyUrdu.includes('پختہبکری');
                            })
                            .map((key, idx) => {
                              // Map English keys to Urdu names
                              const urduMap = {
                                'Cash': 'نقدی',
                                'Rent': 'کرایه',
                                'Commission': 'کمیشن',
                                'Labor': 'مزدوری',
                                'Wages': 'مزدوری',
                                'Miscellaneous': 'مشیانہ',
                                'Mashiyana': 'مشیانہ',
                                'Market Fee': 'مارکیٹ فیس'
                              };
                              return {
                                fieldName: key,
                                fieldNameUrdu: urduMap[key] || key,
                                order: idx
                              };
                            });
                          console.log('Created fields from expenses:', fieldsFromExpenses);
                          return fieldsFromExpenses.length > 0;
                        }
                        return (expenseFields && expenseFields.length > 0) || (bill.expenseFields && bill.expenseFields.length > 0);
                      })() ? (
                        isAdmin ? (
                          <DragDropContext onDragEnd={onExpenseFieldsDragEnd} onDragStart={() => console.log("Drag started")}>
                            <Droppable droppableId="expenseFields" type="EXPENSE_FIELD">
                              {(provided, snapshot) => (
                                <div
                                  {...provided.droppableProps}
                                  ref={provided.innerRef}
                                  className={`space-y-3 ${snapshot.isDraggingOver ? 'bg-blue-50 rounded-lg p-2' : ''}`}
                                >
                                  {(() => {
                                    console.log('=== ADMIN SECTION - RENDERING FIELDS ===');
                                    console.log('expenseFields state:', expenseFields);
                                    console.log('bill.expenseFields:', bill.expenseFields);

                                    // If expenseFields is empty, create from bill.expenses
                                    let fieldsToProcess = expenseFields.length > 0 ? expenseFields : (bill.expenseFields || []);

                                    if (fieldsToProcess.length === 0 && bill.expenses) {
                                      console.log('⚠️ Creating fields from bill.expenses for admin section');
                                      const urduMap = {
                                        'Cash': 'نقدی',
                                        'Rent': 'کرایه',
                                        'Commission': 'کمیشن',
                                        'Labor': 'مزدوری',
                                        'Wages': 'مزدوری',
                                        'Miscellaneous': 'مشیانہ',
                                        'Mashiyana': 'مشیانہ',
                                        'Market Fee': 'مارکیٹ فیس'
                                      };
                                      fieldsToProcess = Object.keys(bill.expenses)
                                        .filter(key => {
                                          const keyLower = key.toLowerCase();
                                          const keyUrdu = key.replace(/\s/g, '');
                                          return key !== 'Vehicle' &&
                                            key !== 'ActualVehicle' &&
                                            key !== 'ItemName' &&
                                            key !== 'ItemUnit' &&
                                            key !== 'ItemQuantity' &&
                                            key !== 'DeliveryMethod' &&
                                            key !== 'GoodsMessageUrdu' &&
                                            // Exclude Raw Sale, Expenses, Net Sale fields
                                            keyLower !== 'raw sale' &&
                                            keyLower !== 'rawsale' &&
                                            keyLower !== 'expenses' &&
                                            keyLower !== 'net sale' &&
                                            keyLower !== 'netsale' &&
                                            key !== 'Raw Sale' &&
                                            key !== 'Expenses' &&
                                            key !== 'Net Sale' &&
                                            !keyUrdu.includes('خامبکری') &&
                                            !keyUrdu.includes('اخراجات') &&
                                            !keyUrdu.includes('پختہبکری');
                                        })
                                        .map((key, idx) => ({
                                          fieldName: key,
                                          fieldNameUrdu: urduMap[key] || key,
                                          order: idx
                                        }));
                                      console.log('Created fieldsToProcess:', fieldsToProcess);
                                    }

                                    // Normalize fields before rendering - FORCE Urdu for Labor and Miscellaneous
                                    const fieldsToRender = fieldsToProcess.map(field => {
                                      console.log('Admin - Processing field for rendering:', {
                                        fieldName: field.fieldName,
                                        fieldNameUrdu: field.fieldNameUrdu,
                                        fullField: field
                                      });

                                      // Always force Urdu for Labor/Wages
                                      if (field.fieldName === 'Labor' || field.fieldName === 'Wages') {
                                        const normalized = { ...field, fieldName: 'Labor', fieldNameUrdu: 'مزدوری' };
                                        console.log('Admin - ✓ Normalized Labor/Wages:', normalized);
                                        return normalized;
                                      }
                                      // Always force Urdu for Miscellaneous/Mashiyana
                                      if (field.fieldName === 'Miscellaneous' || field.fieldName === 'Mashiyana') {
                                        const normalized = { ...field, fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ' };
                                        console.log('Admin - ✓ Normalized Miscellaneous/Mashiyana:', normalized);
                                        return normalized;
                                      }
                                      return field;
                                    });

                                    console.log('Admin - Final fieldsToRender:', fieldsToRender);
                                    console.log('Admin - Labor/Misc fields:', fieldsToRender.filter(f =>
                                      f.fieldName === 'Labor' || f.fieldName === 'Wages' ||
                                      f.fieldName === 'Miscellaneous' || f.fieldName === 'Mashiyana'
                                    ));

                                    return fieldsToRender;
                                  })().filter(field => {
                                    const urduName = (field.fieldNameUrdu || '').replace(/\s/g, '');
                                    const fieldName = (field.fieldName || '').toLowerCase();
                                    const isVehicle =
                                      field.fieldName === 'Vehicle' ||
                                      field.fieldName === 'ActualVehicle' ||
                                      urduName.includes('گاڑی');
                                    const isGoodsMessage =
                                      field.fieldName === 'GoodsMessageUrdu' ||
                                      urduName.includes('مالکاپیغام') ||
                                      urduName.includes('آپکامالبذریعہبلٹی');
                                    // Exclude Raw Sale, Expenses, Net Sale fields (خام بکری، اخراجات، پختہ بکری)
                                    const isRawSale = 
                                      fieldName === 'raw sale' || 
                                      fieldName === 'rawsale' ||
                                      urduName.includes('خامبکری') ||
                                      field.fieldName === 'Raw Sale';
                                    const isExpenses = 
                                      fieldName === 'expenses' ||
                                      urduName.includes('اخراجات') ||
                                      field.fieldName === 'Expenses';
                                    const isNetSale = 
                                      fieldName === 'net sale' || 
                                      fieldName === 'netsale' ||
                                      urduName.includes('پختہبکری') ||
                                      field.fieldName === 'Net Sale';
                                    // Exclude Vehicle, GoodsMessageUrdu, Raw Sale, Expenses, and Net Sale from expenses list
                                    return !isVehicle && !isGoodsMessage && !isRawSale && !isExpenses && !isNetSale;
                                  }).map((field, idx) => {
                                    // Debug: Log ALL fields to see structure
                                    console.log(`Admin section - Field #${idx}:`, {
                                      fieldName: field.fieldName,
                                      fieldNameUrdu: field.fieldNameUrdu,
                                      fieldNameType: typeof field.fieldName,
                                      fieldNameUrduType: typeof field.fieldNameUrdu,
                                      fieldNameLength: field.fieldName?.length,
                                      fieldNameUrduLength: field.fieldNameUrdu?.length,
                                      fieldNameTrimmed: String(field.fieldName || '').trim(),
                                      fieldNameUrduTrimmed: String(field.fieldNameUrdu || '').trim(),
                                      fullFieldObject: field,
                                      isLabor: field.fieldName === 'Labor' || field.fieldName === 'Wages',
                                      isMiscellaneous: field.fieldName === 'Miscellaneous' || field.fieldName === 'Mashiyana'
                                    });
                                    let value = bill.expenses?.[field.fieldName];
                                    // Fallback: sometimes value is stored against Urdu field name
                                    if ((value === undefined || value === null) && field.fieldNameUrdu) {
                                      value = bill.expenses?.[field.fieldNameUrdu];
                                    }
                                    if (value === undefined || value === null) value = 0;
                                    // Remove "Rs." if already present in value
                                    if (typeof value === 'string' && value.includes('Rs.')) {
                                      value = value.replace(/Rs\.?\s*/gi, '').trim();
                                    }
                                    value = typeof value === 'string' ? parseFloat(value) || 0 : value;
                                    const displayValue = typeof value === 'number' ? value.toLocaleString() : value;
                                    const uniqueId = `expense-${idx}-${field.fieldName || field.fieldNameUrdu || 'field'}-${field.order || idx}`;
                                    // Check if this is Laga field to hide completely (not shown on bill)
                                    const isLaga = 
                                      String(field.fieldName || '').toLowerCase() === 'laga' ||
                                      String(field.fieldNameUrdu || '').trim() === 'لاگا' ||
                                      String(field.fieldName || '').trim() === 'Laga';
                                    // Don't render laga field at all on bill
                                    if (isLaga) {
                                      return null;
                                    }
                                    return (
                                      <Draggable key={uniqueId} draggableId={uniqueId} index={idx}>
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...(isAdmin ? provided.dragHandleProps : {})}
                                            className={`flex items-center py-2 px-2 rounded-lg transition-all ${snapshot.isDragging ? 'bg-indigo-100 border-2 border-indigo-500 shadow-lg z-50 rotate-2' : 'hover:bg-gray-50 border-2 border-transparent'} ${isAdmin ? 'cursor-move' : ''}`}
                                            style={provided.draggableProps.style}
                                          >
                                            <div className={`flex justify-between items-center w-full gap-8 ${language === 'en' && !showBoth ? 'flex-row' : ''}`}>
                                              {/* Text (label) - Left in English, Right in Urdu */}
                                              <div className={`flex-1 ${language === 'en' && !showBoth ? 'text-left' : 'text-right'}`}>
                                                {(language === 'en' || showBoth) && (
                                                  <span
                                                    className="text-gray-700 font-medium"
                                                    dir="ltr"
                                                    dangerouslySetInnerHTML={{
                                                      __html: (() => {
                                                        // For English display, show English names
                                                        const fieldName = String(field.fieldName || '').trim();
                                                        return (fieldName || '') + '.';
                                                      })()
                                                    }}
                                                  ></span>
                                                )}
                                                {(language === 'ur' || showBoth) && (
                                                  <span
                                                    className="text-gray-700 font-medium"
                                                    dangerouslySetInnerHTML={{
                                                      __html: (() => {
                                                        // DIRECT CHECK - No conditions, just check fieldName and return Urdu
                                                        const originalFieldName = field.fieldName;
                                                        const fieldName = String(field.fieldName || '').trim().toLowerCase();
                                                        const fieldNameUrdu = String(field.fieldNameUrdu || '').trim();

                                                        console.log('🔵 Admin Urdu rendering - field:', {
                                                          originalFieldName,
                                                          fieldName,
                                                          fieldNameUrdu,
                                                          fieldNameLower: fieldName,
                                                          willCheckLabor: fieldName === 'labor' || fieldName === 'wages',
                                                          willCheckMisc: fieldName === 'miscellaneous' || fieldName === 'mashiyana'
                                                        });

                                                        // FORCE URDU FOR LABOR AND MISCELLANEOUS - DIRECT CHECK
                                                        if (fieldName === 'labor' || fieldName === 'wages' || originalFieldName === 'Labor' || originalFieldName === 'Wages') {
                                                          console.log('✅✅✅ Admin - FORCING مزدوری for', originalFieldName);
                                                          return 'مزدوری.';
                                                        }
                                                        // FORCE URDU FOR MISCELLANEOUS - DIRECT CHECK
                                                        if (fieldName === 'miscellaneous' || fieldName === 'mashiyana' || originalFieldName === 'Miscellaneous' || originalFieldName === 'Mashiyana') {
                                                          console.log('✅✅✅ Admin - FORCING مشیانہ for', originalFieldName);
                                                          return 'مشیانہ.';
                                                        }
                                                        // For all other fields, use Urdu name if available
                                                        const result = (fieldNameUrdu || originalFieldName || '') + '.';
                                                        console.log('Admin - Returning default:', result, 'for', originalFieldName);
                                                        return result;
                                                      })(),
                                                    }}
                                                  ></span>
                                                )}
                                              </div>

                                              {/* Value - Right in English, Right in Urdu (unchanged) */}
                                              <div className={`min-w-[60px] ${language === 'en' && !showBoth ? 'text-right' : 'text-right'}`}>
                                                <span className="font-semibold text-gray-900">
                                                  {displayValue}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </DragDropContext>
                        ) : (
                          (() => {
                            console.log('=== NON-ADMIN SECTION - RENDERING FIELDS ===');
                            console.log('expenseFields state:', expenseFields);
                            console.log('bill.expenseFields:', bill.expenseFields);
                            console.log('language:', language, 'showBoth:', showBoth);

                            // If expenseFields is empty, create from bill.expenses
                            let fieldsToProcess = expenseFields.length > 0 ? expenseFields : (bill.expenseFields || []);

                            if (fieldsToProcess.length === 0 && bill.expenses) {
                              console.log('⚠️ Creating fields from bill.expenses for non-admin section');
                              const urduMap = {
                                'Cash': 'نقدی',
                                'Rent': 'کرایه',
                                'Commission': 'کمیشن',
                                'Labor': 'مزدوری',
                                'Wages': 'مزدوری',
                                'Miscellaneous': 'مشیانہ',
                                'Mashiyana': 'مشیانہ',
                                'Market Fee': 'مارکیٹ فیس'
                              };
                              fieldsToProcess = Object.keys(bill.expenses)
                                .filter(key => {
                                  const keyLower = key.toLowerCase();
                                  const keyUrdu = key.replace(/\s/g, '');
                                  return key !== 'Vehicle' &&
                                    key !== 'ActualVehicle' &&
                                    key !== 'ItemName' &&
                                    key !== 'ItemUnit' &&
                                    key !== 'ItemQuantity' &&
                                    key !== 'DeliveryMethod' &&
                                    key !== 'GoodsMessageUrdu' &&
                                    // Exclude Raw Sale, Expenses, Net Sale fields
                                    keyLower !== 'raw sale' &&
                                    keyLower !== 'rawsale' &&
                                    keyLower !== 'expenses' &&
                                    keyLower !== 'net sale' &&
                                    keyLower !== 'netsale' &&
                                    key !== 'Raw Sale' &&
                                    key !== 'Expenses' &&
                                    key !== 'Net Sale' &&
                                    !keyUrdu.includes('خامبکری') &&
                                    !keyUrdu.includes('اخراجات') &&
                                    !keyUrdu.includes('پختہبکری');
                                })
                                .map((key, idx) => ({
                                  fieldName: key,
                                  fieldNameUrdu: urduMap[key] || key,
                                  order: idx
                                }));
                              console.log('Created fieldsToProcess:', fieldsToProcess);
                            }

                            // Normalize fields before rendering - FORCE Urdu for Labor and Miscellaneous
                            const fieldsToRender = fieldsToProcess.map(field => {
                              console.log('Non-admin - Processing field for rendering:', {
                                fieldName: field.fieldName,
                                fieldNameUrdu: field.fieldNameUrdu,
                                fullField: field
                              });

                              // Always force Urdu for Labor/Wages
                              if (field.fieldName === 'Labor' || field.fieldName === 'Wages') {
                                const normalized = { ...field, fieldName: 'Labor', fieldNameUrdu: 'مزدوری' };
                                console.log('Non-admin - ✓ Normalized Labor/Wages:', normalized);
                                return normalized;
                              }
                              // Always force Urdu for Miscellaneous/Mashiyana
                              if (field.fieldName === 'Miscellaneous' || field.fieldName === 'Mashiyana') {
                                const normalized = { ...field, fieldName: 'Miscellaneous', fieldNameUrdu: 'مشیانہ' };
                                console.log('Non-admin - ✓ Normalized Miscellaneous/Mashiyana:', normalized);
                                return normalized;
                              }
                              return field;
                            });

                            console.log('Non-admin - Final fieldsToRender:', fieldsToRender);
                            console.log('Non-admin - Labor/Misc fields:', fieldsToRender.filter(f =>
                              f.fieldName === 'Labor' || f.fieldName === 'Wages' ||
                              f.fieldName === 'Miscellaneous' || f.fieldName === 'Mashiyana'
                            ));

                            return fieldsToRender;
                          })().filter(field => {
                            const urduName = (field.fieldNameUrdu || '').replace(/\s/g, '');
                            const fieldName = (field.fieldName || '').toLowerCase();
                            const isVehicle =
                              field.fieldName === 'Vehicle' ||
                              field.fieldName === 'ActualVehicle' ||
                              urduName.includes('گاڑی');
                            const isGoodsMessage =
                              field.fieldName === 'GoodsMessageUrdu' ||
                              urduName.includes('مالکاپیغام') ||
                              urduName.includes('آپکامالبذریعہبلٹی');
                            // Exclude Raw Sale, Expenses, Net Sale fields (خام بکری، اخراجات، پختہ بکری)
                            const isRawSale = 
                              fieldName === 'raw sale' || 
                              fieldName === 'rawsale' ||
                              urduName.includes('خامبکری') ||
                              field.fieldName === 'Raw Sale';
                            const isExpenses = 
                              fieldName === 'expenses' ||
                              urduName.includes('اخراجات') ||
                              field.fieldName === 'Expenses';
                            const isNetSale = 
                              fieldName === 'net sale' || 
                              fieldName === 'netsale' ||
                              urduName.includes('پختہبکری') ||
                              field.fieldName === 'Net Sale';
                            // Exclude Vehicle, GoodsMessageUrdu, Raw Sale, Expenses, and Net Sale from expenses list
                            return !isVehicle && !isGoodsMessage && !isRawSale && !isExpenses && !isNetSale;
                          }).map((field, idx) => {
                            // Debug: Log ALL fields to see structure
                            console.log(`Non-admin section - Field #${idx}:`, {
                              fieldName: field.fieldName,
                              fieldNameUrdu: field.fieldNameUrdu,
                              fieldNameType: typeof field.fieldName,
                              fieldNameUrduType: typeof field.fieldNameUrdu,
                              fieldNameLength: field.fieldName?.length,
                              fieldNameUrduLength: field.fieldNameUrdu?.length,
                              fieldNameTrimmed: String(field.fieldName || '').trim(),
                              fieldNameUrduTrimmed: String(field.fieldNameUrdu || '').trim(),
                              fullFieldObject: field,
                              language: language,
                              showBoth: showBoth,
                              isLabor: field.fieldName === 'Labor' || field.fieldName === 'Wages',
                              isMiscellaneous: field.fieldName === 'Miscellaneous' || field.fieldName === 'Mashiyana'
                            });
                            let value = bill.expenses?.[field.fieldName];
                            // Fallback: value might be stored under Urdu key
                            if ((value === undefined || value === null) && field.fieldNameUrdu) {
                              value = bill.expenses?.[field.fieldNameUrdu];
                            }
                            if (value === undefined || value === null) value = 0;
                            if (typeof value === 'string' && value.includes('Rs.')) {
                              value = value.replace(/Rs\.?\s*/gi, '').trim();
                            }
                            value = typeof value === 'string' ? parseFloat(value) || 0 : value;
                            const displayValue = typeof value === 'number' ? value.toLocaleString() : value;
                            // Check if this is Laga field to hide completely (not shown on bill)
                            const isLaga = 
                              String(field.fieldName || '').toLowerCase() === 'laga' ||
                              String(field.fieldNameUrdu || '').trim() === 'لاگا' ||
                              String(field.fieldName || '').trim() === 'Laga';
                            // Don't render laga field at all on bill
                            if (isLaga) {
                              return null;
                            }
                            return (
                              <div key={idx} className={`flex items-center py-2`}>
                                <div className={`flex justify-between items-center w-full gap-8 ${language === 'en' && !showBoth ? 'flex-row' : ''}`}>
                                  {/* Text (label) - Left in English, Right in Urdu */}
                                  <div className={`flex-1 ${language === 'en' && !showBoth ? 'text-left' : 'text-right'}`}>

                                    {/* --- English Label (Only English mode OR showBoth) --- */}
                                    {language === 'en' && !showBoth && (
                                      <span
                                        className="text-gray-700 font-medium"
                                        dir="ltr"
                                        dangerouslySetInnerHTML={{
                                          __html: (() => {
                                            // For English display, show English names
                                            const fieldName = String(field.fieldName || '').trim();
                                            return (fieldName || '') + '.';
                                          })()
                                        }}
                                      ></span>
                                    )}

                                    {/* --- Urdu Label (Only Urdu mode OR showBoth) --- */}
                                    {language === 'ur' && !showBoth && (
                                      <span
                                        className="text-gray-700 font-medium"
                                        dangerouslySetInnerHTML={{
                                          __html: (() => {
                                            // FORCE URDU FOR LABOR AND MISCELLANEOUS - NO CONDITIONS
                                            const originalFieldName = String(field.fieldName || '').trim();
                                            const fieldName = originalFieldName.toLowerCase();

                                            console.log('🟢 Non-admin Urdu rendering - field:', {
                                              originalFieldName,
                                              fieldName,
                                              fieldNameUrdu: field.fieldNameUrdu,
                                              language,
                                              showBoth,
                                              fullField: field
                                            });

                                            // ALWAYS return Urdu for Labor/Wages - DIRECT CHECK
                                            if (fieldName === 'labor' || fieldName === 'wages' || originalFieldName === 'Labor' || originalFieldName === 'Wages') {
                                              console.log('✅✅✅ Non-admin - FORCING مزدوری for', originalFieldName);
                                              return 'مزدوری.';
                                            }

                                            // ALWAYS return Urdu for Miscellaneous/Mashiyana - DIRECT CHECK
                                            if (fieldName === 'miscellaneous' || fieldName === 'mashiyana' || originalFieldName === 'Miscellaneous' || originalFieldName === 'Mashiyana') {
                                              console.log('✅✅✅ Non-admin - FORCING مشیانہ for', originalFieldName);
                                              return 'مشیانہ.';
                                            }

                                            // For all other fields, use Urdu name if available
                                            const fieldNameUrdu = String(field.fieldNameUrdu || '').trim();
                                            const result = (fieldNameUrdu || originalFieldName) + '.';
                                            console.log('Non-admin - Returning default:', result, 'for', originalFieldName);
                                            return result;
                                          })(),
                                        }}
                                      ></span>
                                    )}

                                    {/* --- ShowBoth = both English + Urdu --- */}
                                    {showBoth && (
                                      <div className="flex flex-col text-right leading-tight">
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: (() => {
                                              // For English display in showBoth mode, show English names
                                              const fieldName = String(field.fieldName || '').trim();
                                              return (fieldName || '') + '.';
                                            })()
                                          }}
                                          className="text-gray-700 font-medium"
                                        ></span>

                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: (() => {
                                              // DIRECT CHECK - case-insensitive
                                              const fieldName = String(field.fieldName || '').trim().toLowerCase();

                                              console.log('ShowBoth Urdu rendering - field:', {
                                                originalFieldName: field.fieldName,
                                                fieldName,
                                                fieldNameUrdu: field.fieldNameUrdu
                                              });

                                              // FORCE URDU FOR LABOR AND MISCELLANEOUS - DIRECT CHECK
                                              const originalFieldName = String(field.fieldName || '').trim();
                                              if (fieldName === 'labor' || fieldName === 'wages' || originalFieldName === 'Labor' || originalFieldName === 'Wages') {
                                                console.log('✅✅✅ ShowBoth - FORCING مزدوری for', originalFieldName);
                                                return 'مزدوری.';
                                              }
                                              // FORCE URDU FOR MISCELLANEOUS - DIRECT CHECK
                                              if (fieldName === 'miscellaneous' || fieldName === 'mashiyana' || originalFieldName === 'Miscellaneous' || originalFieldName === 'Mashiyana') {
                                                console.log('✅✅✅ ShowBoth - FORCING مشیانہ for', originalFieldName);
                                                return 'مشیانہ.';
                                              }
                                              // For other fields
                                              const result = (field.fieldNameUrdu || field.fieldName) + '.';
                                              console.log('Returning default:', result);
                                              return result;
                                            })(),
                                          }}
                                          className="text-gray-700 font-medium"
                                        ></span>
                                      </div>
                                    )}
                                  </div>


                                  {/* Value - Right in English, Right in Urdu (unchanged) */}
                                  <div className={`min-w-[60px] ${language === 'en' && !showBoth ? 'text-right' : 'text-right'}`}>
                                    <span className="font-semibold text-gray-900">
                                      {displayValue}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )
                      ) : (
                        <>
                          {bill.expenses &&
                            Object.keys(bill.expenses)
                              .filter((key) => {
                                const normalized = key.replace(/\s/g, '');
                                return (
                                  key !== 'Vehicle' &&
                                  key !== 'ActualVehicle' &&
                                  key !== 'ItemName' &&
                                  key !== 'ItemUnit' &&
                                  key !== 'ItemQuantity' &&
                                  key !== 'DeliveryMethod' &&
                                  key !== 'GoodsMessageUrdu' &&
                                  !normalized.includes('گاڑی')
                                );
                              })
                              .map((key, idx) => {
                                // Map English keys to Urdu labels when language is Urdu
                                let label = key;
                                if (language === 'ur') {
                                  const normalized = key.toLowerCase().replace(/\s/g, '');
                                  switch (normalized) {
                                    case 'cash':
                                      label = 'نقدی';
                                      break;
                                    case 'rent':
                                      label = 'کرایه';
                                      break;
                                    case 'commission':
                                      label = 'کمیشن';
                                      break;
                                    case 'wages':
                                      label = 'مزدوری';
                                      break;
                                    case 'mashiyana':
                                      label = 'مشیانه';
                                      break;
                                    case 'marketfee':
                                      label = 'مارکیٹ فیس';
                                      break;
                                    default:
                                      label = key;
                                  }
                                }

                                let value = bill.expenses[key] ?? 0;
                                if (typeof value === 'string' && value.includes('Rs.')) {
                                  value = value.replace(/Rs\.?\s*/gi, '').trim();
                                }
                                value = typeof value === 'string' ? parseFloat(value) || 0 : value;
                                const displayValue = typeof value === 'number' ? value.toLocaleString() : value;
                                return (
                                  <div key={idx} className="flex items-center py-2">
                                    <div className="flex justify-between items-center w-full gap-8">
                                      <div className="flex-1 text-right">
                                        <span className="text-gray-700 font-medium" dir="rtl">
                                          {label}.
                                        </span>
                                      </div>
                                      <div className="min-w-[60px] text-right">
                                        <span className="font-semibold text-gray-900">
                                          {displayValue}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                        </>
                      )}
                      <div className="flex justify-between items-center border-t-2 border-gray-400 py-3 mt-3">
                        {/* Text (label) left side, value right side – same pattern as rows above */}
                        <div className="flex-1 text-right">
                          {(language === 'en' || showBoth) && (
                            <span className="font-bold text-red-600 text-lg" dir="ltr">Total Amount -</span>
                          )}
                          {(language === 'ur' || showBoth) && (
                            <span className="text-xl font-bold text-red-600" dir="rtl">کل رقم -</span>
                          )}
                        </div>
                        <div className="min-w-[60px] text-right">
                          <span className="font-bold text-gray-900 text-lg">
                            {typeof totalExpenses === 'number' ? totalExpenses.toLocaleString() : totalExpenses}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap');
        
        /* A4 Size Container Styles */
        .bill-a4-container {
          width: 210mm;
          min-height: 297mm;
          max-width: 100%;
          margin: 0 auto;
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
        }
        
        @media (max-width: 768px) {
          .bill-a4-container {
            width: 100%;
            min-height: auto;
          }
        }
        
        /* Mobile responsive styles */
        @media (max-width: 640px) {
          .bill-content {
            font-size: 14px;
          }
          .bill-field-label {
            font-size: 0.875rem;
          }
          .bill-field-value {
            font-size: 0.875rem;
          }
        }
        
        @media print {
          .print\\:hidden {
            display: none;
          }
          body {
            background: white;
            margin: 0;
            padding: 0;
            overflow: hidden !important;
          }
          
          html {
            overflow: hidden !important;
            height: 297mm !important;
            max-height: 297mm !important;
          }
          @page {
            margin: 0 !important;
            size: A4;
            width: 210mm;
            height: 297mm;
          }
          
          /* Prevent multiple pages */
          @page :first {
            margin: 0 !important;
          }
          
          @page :left {
            margin: 0 !important;
          }
          
          @page :right {
            margin: 0 !important;
          }
          
          /* Remove page borders in print view */
          .max-w-4xl {
            border: none !important;
            border-left: none !important;
            border-right: none !important;
          }
          
          /* Remove wrapper background and padding in print */
          .bill-a4-wrapper {
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            height: 297mm !important;
            max-height: 297mm !important;
            overflow: hidden !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
          
          /* Hide everything after first page */
          .bill-a4-container::after {
            display: none !important;
          }
          
          /* Ensure content fits within A4 page - only show first page */
          .bill-content-print {
            max-width: 210mm !important;
            width: 100% !important;
            height: 100% !important;
            max-height: 275mm !important;
            overflow: hidden !important;
            position: relative !important;
          }
          
          /* Prevent any element from causing page break */
          .bill-content-print * {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Force desktop layout in print - same as desktop view */
          /* Ensure all responsive classes work as desktop in print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Prevent page breaks */
          .bill-content-print {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          /* Force single page - hide overflow */
          .min-h-screen {
            min-height: 297mm !important;
            max-height: 297mm !important;
            height: 297mm !important;
            overflow: hidden !important;
          }
          
          /* Ensure no content goes beyond first page */
          body > * {
            max-height: 297mm !important;
            overflow: hidden !important;
          }
          
          /* Maintain desktop spacing and layout */
          .bill-content-print,
          .bill-content-print * {
            box-sizing: border-box;
          }
          
          /* Force desktop flex layout in print - keep same gap as normal view (1.5rem for desktop) */
          .print-desktop-layout {
            flex-direction: row !important;
            gap: 1.5rem !important;
          }
          
          /* Force desktop order in print */
          .print-sales-left {
            order: 1 !important;
          }
          .print-expenses-right {
            order: 2 !important;
          }
          
          /* Ensure bill-fields-container matches normal view exactly in print */
          .bill-content-print .bill-fields-container {
            margin-bottom: 2rem !important;
            /* Force 2 columns in print view - same as desktop */
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            display: grid !important;
            gap: 1.5rem !important;
          }
          
          /* Ensure columns don't wrap in print view */
          .bill-content-print .bill-fields-container > div {
            grid-column: auto !important;
            width: 100% !important;
          }
          
          /* Preserve gap-12 (3rem) for label-value spacing in print */
          .bill-content-print .bill-fields-container .gap-12 {
            gap: 3rem !important;
          }
        }
      `}</style>
    </>
  );
}

export default BillView;


